import {AxiosResponse} from 'axios';
import {CarePlan} from '../backend/interfaces/care-plan';
import {CarePlanActivitiesGroup} from '../backend/interfaces/care-plan-activities-group';
import {CarePlanActivitiesGroupType} from '../backend/interfaces/care-plan-activities-group-type';
import {CarePlanApiRequest} from '../backend/requests/care-plan';
import {CarePlanActivitiesGroupApiRequest} from '../backend/requests/care-plan-activities-group';
import {CarePlanActivityTypeApiRequest} from '../backend/requests/care-plan-activity-type';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class EntityUserCarePlansRepository {
  // Singleton Instance
  private static _instance: EntityUserCarePlansRepository;

  // Private
  private logger: Logger;

  static get instance(): EntityUserCarePlansRepository {
    if (!this._instance) {
      this._instance = new EntityUserCarePlansRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('EntityUserCarePlansRepository');
  }

  public initCarePlan(entityId: string, userId: string, activitiesSectorId: string): CarePlan {
    return {
      entityId,
      userId,
      activitiesSectorId,
      isActive: true,
    };
  }

  public async getCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string
  ): Promise<CarePlan | null> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}?localize=true`;

    // Get UserCarePlans Sectors from backend
    try {
      const response: AxiosResponse<CarePlan> = await BackendApiManager.instance.get<CarePlan>(url);

      // Return
      return response.data;
    } catch (error: any) {
      if (error && error.response && error.response.status === 404) {
        return null;
      } else {
        throw error;
      }
    }
  }

  public async generateCarePlanByAi(
    entityId: string,
    userId: string,
    activitiesSectorId: string
  ): Promise<{
    explanation: string;
    carePlan: CarePlan;
  }> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}/ai-generated`;

    // Get UserCarePlans Sectors from backend
    try {
      const response: AxiosResponse<{
        explanation: string;
        carePlan: CarePlan;
      }> = await BackendApiManager.instance.get<{
        explanation: string;
        carePlan: CarePlan;
      }>(url);

      // Return
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  public async updateCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string,
    carePlan: CarePlanApiRequest
  ): Promise<CarePlan | null> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}`;

    // Get UserCarePlans Sectors from backend
    if (carePlan.responsibleUserId === undefined) {
      carePlan.responsibleUserId = null;
    }

    const response: AxiosResponse<CarePlan> = await BackendApiManager.instance.patch<CarePlan>(
      url,
      carePlan
    );

    // Return
    return response.data;
  }

  public async deleteCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string
  ): Promise<boolean> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}`;

    // Get UserCarePlans Sectors from backend
    const response: AxiosResponse = await BackendApiManager.instance.delete(url);

    // Return
    return response.status === 204 || response.status === 205 ? true : false;
  }

  public async addActivitiesGroupToCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string,
    data: CarePlanActivitiesGroupApiRequest
  ): Promise<CarePlanActivitiesGroup | null> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}/groups`;

    // Get UserCarePlans Sectors from backend
    const response: AxiosResponse<CarePlanActivitiesGroup> =
      await BackendApiManager.instance.post<CarePlanActivitiesGroup>(url, data);

    // Return
    return response.data;
  }

  public async updateActivitiesGroupOnCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string,
    activitiesGroupEntryId: string,
    data: CarePlanActivitiesGroupApiRequest
  ): Promise<CarePlanActivitiesGroup> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}/groups/${activitiesGroupEntryId}`;

    // Patch
    const response: AxiosResponse<CarePlanActivitiesGroup> =
      await BackendApiManager.instance.patch<CarePlanActivitiesGroup>(url, data);

    // Return
    return response.data;
  }

  public async removeActivitiesGroupOnCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string,
    activitiesGroupEntryId: string
  ): Promise<void> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}/groups/${activitiesGroupEntryId}`;

    // Delete
    await BackendApiManager.instance.delete<CarePlanActivitiesGroup>(url);
  }

  public async addActivityTypeToCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string,
    activitiesGroupEntryId: string,
    data: CarePlanActivityTypeApiRequest
  ): Promise<CarePlanActivitiesGroupType | null> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}/groups/${activitiesGroupEntryId}/types`;

    // Get UserCarePlans Sectors from backend
    const response: AxiosResponse<CarePlanActivitiesGroupType> =
      await BackendApiManager.instance.post<CarePlanActivitiesGroupType>(url, data);

    // Return
    return response.data;
  }

  public async updateActivityTypeOnCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string,
    activitiesGroupEntryId: string,
    activityTypeEntryId: string,
    data: CarePlanActivityTypeApiRequest
  ): Promise<CarePlanActivitiesGroupType> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}/groups/${activitiesGroupEntryId}/types/${activityTypeEntryId}`;

    // Patch
    const response: AxiosResponse<CarePlanActivitiesGroupType> =
      await BackendApiManager.instance.patch<CarePlanActivitiesGroupType>(url, data);

    // Return
    return response.data;
  }

  public async removeActivityTypeOnCarePlan(
    entityId: string,
    userId: string,
    activitiesSectorId: string,
    activitiesGroupEntryId: string,
    activityTypeEntryId: string
  ): Promise<void> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-plans/${activitiesSectorId}/groups/${activitiesGroupEntryId}/types/${activityTypeEntryId}`;

    // Delete
    await BackendApiManager.instance.delete<CarePlanActivitiesGroupType>(url);
  }

  public async addActivitiesGroupActivityToCarePlan(
    entityId: string,
    userIds: Array<string>,
    activitiesSectorId: string,
    activitiesGroupId: string,
    data: CarePlanActivityTypeApiRequest
  ): Promise<CarePlanActivitiesGroupType | null> {
    // Set url
    const url: string = `/entities/${entityId}/care-plans/${activitiesSectorId}/groups/types`;

    // Get UserCarePlans Sectors from backend
    const response: AxiosResponse<CarePlanActivitiesGroupType> =
      await BackendApiManager.instance.post<CarePlanActivitiesGroupType>(url, {
        userIds,
        activitiesGroupId,
        activityTypeId: data.activityTypeId,
        configurations: data.configurations,
      });

    // Return
    return response.data;
  }
}
